import { ContentContext, LocaleContext, RegionContext } from 'Components/hooks/context';
import React, { useContext, useEffect } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import CovidWave from '@reuters-graphics/chart-module-covidwave';
import NoteBox from 'Components/common/NoteBox';
import SmartText from '@reuters-graphics/react-smart-text';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import pickBy from 'lodash/pickBy';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';

const atlas = new AtlasMetadataClient();

const chart = new CovidWave();

const FlatteningCurve = ({ globalStatistics }) => {
  const { distributionRanks } = globalStatistics;
  const { cases } = distributionRanks;

  const { width: windowWidth } = useWindowDimensions();
  const locale = useContext(LocaleContext);
  const content = useContext(ContentContext);
  const regionSlug = useContext(RegionContext);
  const region = atlas.getRegion(regionSlug);
  const countryList = region.countries.map(c => c.isoAlpha2);

  const regionCases = pickBy(cases, (v, k) => countryList.includes(k));

  const chartContainer = React.createRef();

  const resize = debounce(() => {
    if (!chartContainer.current) return;
    chart
      .props({
        height: windowWidth < 600 ? 150 : 200,
      })
      .draw();
  }, 250);

  useEffect(() => {
    chart
      .selection(chartContainer.current)
      .data(regionCases)
      .props({
        locale,
        height: windowWidth < 600 ? 150 : 200,
        thresholdDomain: [0.75, 0.9],
        thresholdRange: {
          color: ['#ccc', '#f68e26', '#de2d26'],
          opacity: [0.3, 0.5, 0.8],
        },
        thresholdText: content.template.flatteningCurve.threshold,
        peakText: {
          ofPeak: content.template.flatteningCurve.ofPeak,
          atPeak: content.template.flatteningCurve.atPeak,
        },
        legendText: {
          max: content.template.flatteningCurve.legendMax,
          min: content.template.flatteningCurve.legendMin,
        },
      })
      .draw();
    return () => chart.destroy();
  }, [windowWidth, locale, regionCases, content, chartContainer]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div className={component}>
      <div className='section-spacer before' />
      <div className='section-spacer-internal before' />
      <div>
        <h2>
          <SmartText
            source={content.template.flatteningCurve.title}
            context={{
              region: region.translations[locale],
            }}
          />
        </h2>
        <SmartText source={content.template.flatteningCurve.intro} multiline />
        <NoteBox
          title={content.template.flatteningCurve.note.title}
          source={content.template.flatteningCurve.note.text}
        />
        <div className='covid-wave-chart-container' ref={chartContainer} />
      </div>
    </div>
  );
};

export default FlatteningCurve;
